import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Utensils, Waves, ChefHat, Wine, Star, Filter } from "lucide-react";
import { Layout } from "@/components/Layout";
import { MenuCard } from "@/components/Cards";
import { menuItems } from "@/data/index";
import { MenuItem } from "@/lib/index";

const CATEGORIES = [
  { id: "todas", label: "Todo el Menú", icon: Utensils },
  { id: "entradas", label: "Entradas", icon: Star },
  { id: "mariscos", label: "Mariscos", icon: Waves },
  { id: "especialidades", label: "Especialidades", icon: ChefHat },
  { id: "bebidas", label: "Bebidas", icon: Wine },
];

const ORIGINS = [
  { id: "todos", label: "Ambos Restaurantes" },
  { id: "Gaviotas", label: "Las Gaviotas (Tradicional)" },
  { id: "Medusas", label: "Medusas (Autor)" },
];

export default function Menu() {
  const [activeCategory, setActiveCategory] = useState("todas");
  const [activeOrigin, setActiveOrigin] = useState("todos");

  const filteredItems = useMemo(() => {
    return menuItems.filter((item) => {
      const matchesCategory = activeCategory === "todas" || item.categoria === activeCategory;
      const matchesOrigin = activeOrigin === "todos" || item.origen === activeOrigin;
      return matchesCategory && matchesOrigin;
    });
  }, [activeCategory, activeOrigin]);

  return (
    <Layout>
      <div className="min-h-screen pb-20">
        {/* Hero Section Gastronómico */}
        <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=2074&auto=format&fit=crop"
              alt="Gastronomía Las Gaviotas"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/60 to-background" />
          </div>

          <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl font-semibold mb-4 text-primary"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Nuestra Gastronomía
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl text-muted-foreground"
            >
              Desde la tradición sinaloense de Las Gaviotas hasta la sofisticación contemporánea de Medusas.
            </motion.p>
          </div>
        </section>

        {/* Filters & Navigation */}
        <section className="container mx-auto px-4 -mt-8 relative z-20">
          <div className="bg-card border border-border rounded-3xl p-6 md:p-8 shadow-xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              {/* Category Filter */}
              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 text-sm font-medium border ${
                      activeCategory === cat.id
                        ? "bg-primary text-primary-foreground border-primary shadow-lg scale-105"
                        : "bg-background text-muted-foreground border-border hover:border-accent hover:bg-accent/10"
                    }`}
                  >
                    <cat.icon size={16} />
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Origin Filter */}
              <div className="flex items-center gap-4 bg-muted/50 p-1.5 rounded-2xl border border-border">
                {ORIGINS.map((origin) => (
                  <button
                    key={origin.id}
                    onClick={() => setActiveOrigin(origin.id)}
                    className={`px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition-all ${
                      activeOrigin === origin.id
                        ? "bg-background text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {origin.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Menu Grid */}
        <section className="container mx-auto px-4 mt-16">
          <div className="flex items-center gap-3 mb-8">
            <Filter className="text-accent" size={20} />
            <h2 className="text-2xl font-semibold tracking-tight">
              {activeCategory === "todas" ? "Todos los platillos" : `Especialidades en ${activeCategory}`}
              <span className="ml-3 text-sm font-normal text-muted-foreground">
                ({filteredItems.length} opciones encontradas)
              </span>
            </h2>
          </div>

          <AnimatePresence mode="popLayout">
            {filteredItems.length > 0 ? (
              <motion.div
                layout
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredItems.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  >
                    <MenuCard item={item} />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-20 text-center"
              >
                <div className="bg-muted/30 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Utensils size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-xl font-medium mb-2">No encontramos platillos</h3>
                <p className="text-muted-foreground">
                  Intenta cambiar los filtros para descubrir más sabores de nuestro complejo.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* Information Section */}
        <section className="container mx-auto px-4 mt-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-primary/5 rounded-[2.5rem] p-8 md:p-12 border border-primary/10">
              <div className="mb-6 inline-flex p-3 bg-primary/10 rounded-2xl text-primary">
                <Waves size={24} />
              </div>
              <h3 className="text-3xl font-semibold mb-4 text-primary" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Tradición Las Gaviotas
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Nuestra cocina tradicional se basa en el respeto absoluto al producto del Mar de Cortés.
                Utilizamos técnicas ancestrales como el zarandeado a la leña y salsas que han pasado por
                generaciones en nuestra familia.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-1.5 bg-white rounded-full text-xs font-medium border border-border shadow-sm">
                  Mariscos Frescos
                </span>
                <span className="px-4 py-1.5 bg-white rounded-full text-xs font-medium border border-border shadow-sm">
                  Leña Natural
                </span>
                <span className="px-4 py-1.5 bg-white rounded-full text-xs font-medium border border-border shadow-sm">
                  Recetas Familiares
                </span>
              </div>
            </div>

            <div className="bg-secondary/20 rounded-[2.5rem] p-8 md:p-12 border border-secondary/30">
              <div className="mb-6 inline-flex p-3 bg-secondary/30 rounded-2xl text-secondary-foreground">
                <ChefHat size={24} />
              </div>
              <h3 className="text-3xl font-semibold mb-4 text-secondary-foreground" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Vanguardia Medusas
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                En Medusas, el chef interpreta el paisaje de El Maviri a través de platos contemporáneos.
                Mixología de autor, emplatados artísticos y fusiones inesperadas que elevan el sabor local
                a una experiencia gourmet inolvidable.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-1.5 bg-white rounded-full text-xs font-medium border border-border shadow-sm">
                  Mixología
                </span>
                <span className="px-4 py-1.5 bg-white rounded-full text-xs font-medium border border-border shadow-sm">
                  Cena de Autor
                </span>
                <span className="px-4 py-1.5 bg-white rounded-full text-xs font-medium border border-border shadow-sm">
                  Atardeceres
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
